import type { settingsFr } from './fr';

/** Settings zone keys (English). */
export const settingsEn: Record<keyof typeof settingsFr, string> = {
  'settings.title': 'Settings',
  'settings.subtitle': 'Organization, white label, integrations, API keys, recipes and knowledge base.',
  'settings.tab.organization': 'Organization',
  'settings.tab.whitelabel': 'White label',
  'settings.tab.integrations': 'Integrations',
  'settings.tab.apikeys': 'API keys',
  'settings.tab.recipes': 'Recipes',
  'settings.tab.knowledge': 'Knowledge base',
  'settings.tab.danger': 'Danger zone',
  'settings.save': 'Save',
  'settings.saved': 'Changes saved',
  'settings.savedPartial': 'The server does not support all or part of these settings yet: they remain unchanged.',
  'settings.reset': 'Discard changes',
  'settings.cancel': 'Cancel',
  'settings.close': 'Close',
  'settings.actions': 'Actions',
  'settings.readOnly': 'You can view these settings but your role does not allow editing them.',
  'settings.error.generic': 'The operation failed. Please try again.',

  'settings.secret.once': 'Copy this secret now: it will never be shown again. If you lose it, you will have to generate a new one.',
  'settings.secret.copy': 'Copy',
  'settings.secret.copiedShort': 'Copied',
  'settings.secret.copied': 'Copied to clipboard',
  'settings.secret.copyFailed': 'Copy failed: select the text manually.',
  'settings.secret.done': 'I have copied the secret, close',

  'settings.org.title': 'Organization',
  'settings.org.desc': 'Identity, time zone, default language, data retention and storage region.',
  'settings.org.name': 'Organization name',
  'settings.org.nameRequired': 'The name is required.',
  'settings.org.domain': 'Domain',
  'settings.org.domainHint': 'The organization\'s email domain (e.g. tassili.dz).',
  'settings.org.domainInvalid': 'Invalid domain (e.g. example.com).',
  'settings.org.timezone': 'Time zone',
  'settings.org.locale': 'Default language',
  'settings.org.localeHint': 'Applied to new members.',
  'settings.org.auditRetention': 'Audit log retention',
  'settings.org.auditRetentionHint': 'Older events are permanently deleted.',
  'settings.org.recordingRetention': 'Session recording retention',
  'settings.org.recordingRetentionHint': 'Recordings are encrypted, then deleted when they expire.',
  'settings.org.days': '{count} days',
  'settings.org.region': 'Data storage region',
  'settings.org.regionHint': 'Where logs, recordings and documents are hosted.',
  'settings.org.region.eu': 'Europe (Frankfurt)',
  'settings.org.region.mena': 'North Africa / Middle East (Algiers)',
  'settings.org.regionConfirmTitle': 'Change the storage region?',
  'settings.org.regionConfirmDesc': 'The organization\'s data will be migrated to "{region}". This can take several hours and is recorded in the audit log.',
  'settings.org.regionConfirm': 'Migrate data',

  'settings.wl.title': 'White label',
  'settings.wl.desc': 'Logo, colors and custom domain of the console for your customers.',
  'settings.wl.enable': 'Enable white label',
  'settings.wl.enableDesc': 'Replaces the Siraj identity with yours in the console and emails.',
  'settings.wl.logo': 'Logo',
  'settings.wl.logoAlt': 'Organization logo',
  'settings.wl.noLogo': 'No logo',
  'settings.wl.logoUpload': 'Choose a logo',
  'settings.wl.logoRemove': 'Remove',
  'settings.wl.logoHint': 'PNG, JPEG, SVG or WebP, {max} KB maximum.',
  'settings.wl.logoType': 'Unsupported format: use PNG, JPEG, SVG or WebP.',
  'settings.wl.logoSize': 'File too large ({max} KB maximum).',
  'settings.wl.logoRead': 'This file could not be read.',
  'settings.wl.primary': 'Primary color',
  'settings.wl.primaryHint': 'Sidebar and avatars; white text on top.',
  'settings.wl.accent': 'Accent color',
  'settings.wl.accentHint': 'Buttons and active items.',
  'settings.wl.picker': 'Color picker: {label}',
  'settings.wl.hexInvalid': 'Invalid hex code (e.g. #0F1623).',
  'settings.wl.contrast.title': 'Contrast check (WCAG AA)',
  'settings.wl.contrast.primary': 'White text on the primary color',
  'settings.wl.contrast.accent': 'Text on the accent color',
  'settings.wl.contrast.pass': 'Passes',
  'settings.wl.contrast.fail': 'Too low',
  'settings.wl.contrast.primaryFail': 'White text contrast is below {min}:1: the primary color is too light to be readable.',
  'settings.wl.contrast.fix': 'Fix automatically',
  'settings.wl.domain': 'Custom domain',
  'settings.wl.domainHint': 'The console address for your customers (e.g. console.example.com).',
  'settings.wl.preview': 'Live preview',
  'settings.wl.previewDesc': 'The console in your brand colors.',
  'settings.wl.previewAlt': 'Console preview with the chosen logo and colors',
  'settings.wl.previewButton': 'New session',
  'settings.wl.previewDisabled': 'White label is disabled: this preview is not applied yet.',
  'settings.wl.dns.title': 'Domain verification',
  'settings.wl.dns.desc': 'Prove you control the domain with a DNS record.',
  'settings.wl.dns.none': 'Save a custom domain to get the DNS record to create.',
  'settings.wl.dns.instructions': 'Add this TXT record at your DNS provider for {domain}, then run the verification.',
  'settings.wl.dns.type': 'Type',
  'settings.wl.dns.host': 'Name',
  'settings.wl.dns.value': 'Value',
  'settings.wl.dns.copy': 'Copy value',
  'settings.wl.dns.check': 'Verify now',
  'settings.wl.dns.verified': 'Verified',
  'settings.wl.dns.pending': 'Pending',
  'settings.wl.dns.saveFirst': 'Save the new domain before verifying it.',
  'settings.wl.dns.lastCheck': 'Last check: {date}. DNS propagation can take a few minutes.',
  'settings.wl.dns.verifiedToast': 'Domain verified',
  'settings.wl.dns.pendingToast': 'DNS record not found yet: try again in a few moments.',

  'settings.int.title': 'Ticketing',
  'settings.int.desc': 'Automatically attach the session report to the ticket in your helpdesk tool.',
  'settings.int.desc.freshdesk': 'Session report attached to the Freshdesk ticket',
  'settings.int.desc.zendesk': 'Session report attached to the Zendesk ticket',
  'settings.int.desc.glpi': 'Session report attached to the GLPI ticket',
  'settings.int.desc.jira_sm': 'Session report attached to the Jira Service Management request',
  'settings.int.status.connected': 'Connected',
  'settings.int.status.disconnected': 'Not connected',
  'settings.int.status.error': 'Error',
  'settings.int.connectedSince': 'Connected {date}',
  'settings.int.notConnected': 'Not connected yet',
  'settings.int.testOk': 'last test passed ({ms} ms)',
  'settings.int.testFailed': 'last test failed',
  'settings.int.testFirst': 'Test the connection before enabling the integration.',
  'settings.int.configure': 'Configure',
  'settings.int.view': 'View',
  'settings.int.connect': 'Connect',
  'settings.int.disconnect': 'Disconnect',
  'settings.int.connectedToast': '{name} connected',
  'settings.int.disconnectedToast': '{name} disconnected',
  'settings.int.drawerDesc': 'Service access, connection test and field mapping.',
  'settings.int.baseUrl': 'Service URL',
  'settings.int.baseUrlHint': 'HTTPS address of your instance.',
  'settings.int.urlInvalid': 'The URL must start with https://.',
  'settings.int.account': 'Service account',
  'settings.int.secret': 'Token or API key',
  'settings.int.secretHint': 'Stored encrypted, never shown again.',
  'settings.int.secretKeep': 'A token is stored. Leave empty to keep it.',
  'settings.int.attach': 'Attach the session report',
  'settings.int.attachDesc': 'Automatically adds the report to the ticket when the session ends.',
  'settings.int.mapping': 'Field mapping',
  'settings.int.mappingDesc': 'Ticket field that receives each piece of the report.',
  'settings.int.mappingFor': 'Ticket field for: {field}',
  'settings.int.field.subject': 'Subject',
  'settings.int.field.description': 'Description',
  'settings.int.field.technician': 'Technician',
  'settings.int.field.duration': 'Duration',
  'settings.int.field.summary': 'AI summary',
  'settings.int.field.device': 'Device',
  'settings.int.field.rating': 'Rating',
  'settings.int.test': 'Test connection',
  'settings.int.saveBeforeTest': 'Save your changes before testing.',
  'settings.int.testOkLong': 'Connection successful ({ms} ms).',
  'settings.int.testError.ok': 'Connection successful.',
  'settings.int.testError.invalid_url': 'Invalid URL: an HTTPS address is required.',
  'settings.int.testError.unauthorized': 'Access denied: check the account and token.',
  'settings.int.testError.unreachable': 'Service unreachable: check the address and the network.',

  'settings.wh.title': 'Outgoing webhooks',
  'settings.wh.desc': 'Receive Siraj events in your systems. Every delivery is signed (HMAC-SHA256) with the webhook secret.',
  'settings.wh.add': 'Add a webhook',
  'settings.wh.addDesc': 'Siraj will send a signed POST request to this address.',
  'settings.wh.create': 'Create webhook',
  'settings.wh.url': 'Destination URL',
  'settings.wh.urlInvalid': 'The URL must start with https://.',
  'settings.wh.events': 'Events',
  'settings.wh.enabled': 'Active',
  'settings.wh.toggle': 'Enable or disable the webhook {url}',
  'settings.wh.last': 'Last delivery',
  'settings.wh.never': 'Never',
  'settings.wh.empty': 'No webhook configured.',
  'settings.wh.secretHint': 'Secret ending in {hint}',
  'settings.wh.secretTitle': 'Webhook signing secret',
  'settings.wh.secretDesc': 'Use it to verify the signature of incoming requests.',
  'settings.wh.rotate': 'Rotate secret',
  'settings.wh.rotateTitle': 'Rotate the secret?',
  'settings.wh.rotateDesc': 'The old secret stops working immediately. Update your receiver with the new one.',
  'settings.wh.delete': 'Delete webhook',
  'settings.wh.deleteTitle': 'Delete this webhook?',
  'settings.wh.deleted': 'Webhook deleted',
  'settings.wh.deliveries': 'Delivery history',
  'settings.wh.noDeliveries': 'No deliveries yet.',
  'settings.wh.date': 'Date',
  'settings.wh.event': 'Event',
  'settings.wh.result': 'Result',
  'settings.wh.attempt': 'Attempt',
  'settings.wh.status.success': 'Succeeded',
  'settings.wh.status.failed': 'Failed',
  'settings.wh.replay': 'Replay this delivery',
  'settings.wh.replayed': 'Delivery replayed',

  'settings.keys.title': 'API keys',
  'settings.keys.desc': 'Programmatic access to the Siraj API. Each key has its own scopes and can expire.',
  'settings.keys.create': 'Create a key',
  'settings.keys.createDesc': 'Limit the key to the scopes it actually needs.',
  'settings.keys.generate': 'Generate key',
  'settings.keys.name': 'Name',
  'settings.keys.namePlaceholder': 'E.g. Nightly BI export',
  'settings.keys.expiry': 'Expiration',
  'settings.keys.expiryDays': '{count} days',
  'settings.keys.expiryNever': 'Never',
  'settings.keys.scopes': 'Scopes',
  'settings.keys.scopesHint': 'Choose at least one scope. A key cannot exceed your own permissions.',
  'settings.keys.presetRead': 'Read-only',
  'settings.keys.presetNone': 'Clear all',
  'settings.keys.created': 'Created',
  'settings.keys.lastUsed': 'Last used',
  'settings.keys.neverUsed': 'Never used',
  'settings.keys.expires': 'Expires',
  'settings.keys.noExpiry': 'No expiration',
  'settings.keys.expired': 'Expired',
  'settings.keys.empty': 'No API keys',
  'settings.keys.emptyDesc': 'Create a key to integrate Siraj with your tools.',
  'settings.keys.secretTitle': 'Your new API key',
  'settings.keys.secretDesc': 'It grants access to your organization according to its scopes.',
  'settings.keys.revoke': 'Revoke',
  'settings.keys.revokeNamed': 'Revoke key {name}',
  'settings.keys.revokeTitle': 'Revoke this key?',
  'settings.keys.revokeDesc': 'Applications using "{name}" will lose access immediately. This cannot be undone.',
  'settings.keys.revoked': 'Key revoked',

  'settings.rcp.title': 'Organization recipes',
  'settings.rcp.desc': 'Reusable troubleshooting procedures: approval, signature, then progressive rollout across the fleet.',
  'settings.rcp.mine': 'My recipes',
  'settings.rcp.library': 'Official library',
  'settings.rcp.official': 'Official',
  'settings.rcp.new': 'New recipe',
  'settings.rcp.edit': 'Edit',
  'settings.rcp.editSignedNote': 'This recipe is signed: editing it creates a new version that must be approved again.',
  'settings.rcp.name': 'Name',
  'settings.rcp.description': 'Description',
  'settings.rcp.steps': 'Steps',
  'settings.rcp.stepsHint': 'One step per line.',
  'settings.rcp.status.draft': 'Draft',
  'settings.rcp.status.pending_approval': 'Pending approval',
  'settings.rcp.status.signed': 'Signed',
  'settings.rcp.meta': 'By {author} · updated {date}',
  'settings.rcp.signature': 'signature {sig}…',
  'settings.rcp.approvedBy': 'approved by {name}',
  'settings.rcp.submit': 'Submit for approval',
  'settings.rcp.submitted': 'Recipe submitted for approval',
  'settings.rcp.approve': 'Approve and sign',
  'settings.rcp.signed': 'Recipe signed',
  'settings.rcp.reject': 'Send back to draft',
  'settings.rcp.rejected': 'Recipe sent back to draft',
  'settings.rcp.deploy': 'Deploy',
  'settings.rcp.delete': 'Delete',
  'settings.rcp.deleteTitle': 'Delete this recipe?',
  'settings.rcp.deleted': 'Recipe deleted',
  'settings.rcp.import': 'Add to my organization',
  'settings.rcp.imported': 'Recipe added as a draft',
  'settings.rcp.empty': 'No recipes',
  'settings.rcp.emptyDesc': 'Create a recipe or import one from the official library.',
  'settings.rcp.rollout.title': 'Progressive rollout',
  'settings.rcp.rollout.stages': 'Rollout stages',
  'settings.rcp.rollout.machines': '{count} machine(s)',
  'settings.rcp.rollout.failures': '{failed} failure(s) · {pct}%',
  'settings.rcp.rollout.next': 'Next stage',
  'settings.rcp.rollout.pending': 'Pending',
  'settings.rcp.rollout.status.running': 'Running',
  'settings.rcp.rollout.status.halted': 'Stopped',
  'settings.rcp.rollout.status.completed': 'Completed',
  'settings.rcp.rollout.thresholdLabel': 'Failure threshold (%)',
  'settings.rcp.rollout.thresholdInvalid': 'Between 0 and 100.',
  'settings.rcp.rollout.threshold': 'Automatic stop if more than {pct}% of a stage\'s machines fail.',
  'settings.rcp.rollout.start': 'Start (1 machine)',
  'settings.rcp.rollout.restart': 'Restart rollout',
  'settings.rcp.rollout.advance': 'Go to next stage',
  'settings.rcp.rollout.halt': 'Stop',
  'settings.rcp.rollout.autoHalted': 'Automatic stop: {pct}% failures, above the {threshold}% threshold. The rollout does not continue.',
  'settings.rcp.rollout.autoHaltedToast': 'Rollout stopped automatically: failure threshold exceeded',
  'settings.rcp.rollout.manualHalted': 'Rollout stopped manually.',
  'settings.rcp.rollout.completed': 'Rollout completed on all stages.',
  'settings.rcp.rollout.completedToast': 'Rollout completed',

  'settings.kb.title': 'Knowledge base',
  'settings.kb.desc': 'Your company documents that the AI assistant consults to answer (RAG). They stay private to your organization.',
  'settings.kb.drop': 'Drag your documents here',
  'settings.kb.formats': 'Formats: {formats} · {max} MB maximum per file',
  'settings.kb.add': 'Add documents',
  'settings.kb.added': '"{name}" added, indexing in progress',
  'settings.kb.unsupported': '"{name}": unsupported format.',
  'settings.kb.tooLarge': '"{name}" exceeds {max} MB.',
  'settings.kb.name': 'Document',
  'settings.kb.status': 'Indexing',
  'settings.kb.status.queued': 'Queued',
  'settings.kb.status.indexing': 'Indexing',
  'settings.kb.status.indexed': 'Indexed',
  'settings.kb.status.failed': 'Failed',
  'settings.kb.error.unsupported_type': 'Unsupported format',
  'settings.kb.error.too_large': 'File too large',
  'settings.kb.error.extraction_failed': 'Text could not be extracted (scan or protected file)',
  'settings.kb.chunks': 'Passages',
  'settings.kb.added.col': 'Added',
  'settings.kb.retry': 'Retry indexing {name}',
  'settings.kb.delete': 'Delete {name}',
  'settings.kb.deleteTitle': 'Delete this document?',
  'settings.kb.deleteDesc': '"{name}" will be removed from the index: the assistant will no longer use it.',
  'settings.kb.deleteConfirm': 'Delete',
  'settings.kb.deleted': 'Document deleted',
  'settings.kb.empty': 'No documents',
  'settings.kb.emptyDesc': 'Add procedures, policies or guides to enrich the assistant\'s answers.',
  'settings.kb.statDocs': 'Documents',
  'settings.kb.statInProgress': '{count} being indexed',
  'settings.kb.statChunks': 'Indexed passages',
  'settings.kb.statChunksHint': 'Available to the assistant',
  'settings.kb.kb': 'KB',
  'settings.kb.mb': 'MB',

  'settings.danger.exportTitle': 'Export data',
  'settings.danger.exportDesc': 'Download a JSON export of the organization (settings, members, fleet, policies, sessions and audit log).',
  'settings.danger.export': 'Export data',
  'settings.danger.exported': 'Export downloaded',
  'settings.danger.noExport': 'Exporting requires settings management and audit export permissions.',
  'settings.danger.deleteTitle': 'Delete the organization',
  'settings.danger.deleteDesc': 'Permanently deletes the organization and all its data. Consider exporting your data first.',
  'settings.danger.delete': 'Delete the organization',
  'settings.danger.ownerOnly': 'Only the organization owner can delete it.',
  'settings.danger.step1Title': 'Delete the organization?',
  'settings.danger.step1Warning': 'This action cannot be undone.',
  'settings.danger.consequence1': 'Devices, members, policies and logs are deleted.',
  'settings.danger.consequence2': 'API keys, webhooks and integrations stop working.',
  'settings.danger.consequence3': 'The subscription is canceled with no refund for the current month.',
  'settings.danger.understand': 'I understand this deletion is permanent.',
  'settings.danger.continue': 'Continue',
  'settings.danger.step2Title': 'Final confirmation',
  'settings.danger.step2Desc': 'Type the exact organization name, "{name}", to confirm.',
  'settings.danger.typeName': 'Organization name',
  'settings.danger.confirmDelete': 'Delete permanently',
  'settings.danger.deleted': 'Organization deleted',
  'settings.danger.deleteFailed': 'Deletion failed: check the typed name and your permissions.',

  // Écran « Mon profil » (src/features/profile)
  'profile.account': 'Account',
  'profile.accountDesc': 'Sign-in details of your Siraj account.',
  'profile.name': 'Name',
  'profile.email': 'Email',
  'profile.twoFactor': 'Two-factor authentication',
  'profile.twoFactorOn': 'Enabled',
  'profile.twoFactorOff': 'Disabled',
  'profile.preferences': 'Preferences',
  'profile.preferencesDesc': 'Console language and display theme.',
  'profile.organizations': 'Organizations',
  'profile.organizationsDesc': 'Organizations your account belongs to and your role in each.',
  'profile.active': 'Active',
  'profile.switch': 'Switch',
};
